import { SiteNetwork } from '@domain/graphql.types';
import { TFunction } from 'i18next';
import { FilterCategory, SearchFilters, getActiveCategories, getSelectedNetworkNames } from './searchFilters';

const parseDate = (date: string) => {
  const [year, month] = date.split('-').map(Number);
  return { year, month };
};

// Dates are YYYY-MM-DD: format them in UTC so the month never shifts.
const formatMonth = (date: string, language: string) =>
  new Intl.DateTimeFormat(language, { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(date));

/** "Since Aug 2023", "Until Dec 2025", "2022 – 2023" (whole years) or "Mar 2022 – Jun 2023". */
const openingDateLabel = (openedFrom: string | null, openedTo: string | null, t: TFunction, language: string) => {
  if (openedFrom && openedTo) {
    const from = parseDate(openedFrom);
    const to = parseDate(openedTo);

    if (from.month === 1 && to.month === 12) {
      return from.year === to.year ? String(from.year) : t('siteLookup.filters.chips.openedBetween', { from: from.year, to: to.year });
    }

    return t('siteLookup.filters.chips.openedBetween', { from: formatMonth(openedFrom, language), to: formatMonth(openedTo, language) });
  }

  return openedFrom
    ? t('siteLookup.filters.chips.openedSince', { from: formatMonth(openedFrom, language) })
    : t('siteLookup.filters.chips.openedUntil', { to: formatMonth(openedTo ?? '', language) });
};

/** Label of a filter chip: the category name while inactive, a summary of the applied value otherwise. */
export const getFilterChipLabel = (
  category: FilterCategory,
  filters: SearchFilters,
  catalogue: readonly SiteNetwork[] | undefined,
  t: TFunction,
  language: string,
): string => {
  if (!getActiveCategories(filters).includes(category)) {
    return t(`siteLookup.filters.chips.${category}`);
  }

  switch (category) {
    case 'network': {
      const [name] = getSelectedNetworkNames(filters.networkIds, catalogue);
      // A single network missing from the catalogue (not loaded yet, or unavailable) is still counted.
      return filters.networkIds.length === 1 && name ? name : t('siteLookup.filters.chips.networks', { count: filters.networkIds.length });
    }
    case 'minimumPorts':
      return t('siteLookup.filters.chips.minimumPortsActive', { count: filters.minimumPortsPerStation ?? 0 });
    case 'minimumPower':
      return t('siteLookup.filters.chips.minimumPowerActive', { power: filters.minimumPowerKwPerPort });
    case 'openingDate':
      return openingDateLabel(filters.openedFrom, filters.openedTo, t, language);
    case 'dealership':
      return filters.dealership === 'exclude' ? t('siteLookup.filters.chips.dealershipExclude') : t('siteLookup.filters.chips.dealershipOnly');
  }
};
